// 📂 src/pages/CameraDetail/EnterpriseDVRPlayer.jsx

import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import {
  FiAlertTriangle,
  FiBookmark,
  FiDownload,
  FiMaximize,
  FiMinimize,
  FiX,
  FiMenu,
  FiSearch,
  FiClock,
  FiScissors,
  FiCamera,
  FiZoomIn,
  FiZoomOut,
  FiCrosshair,
  FiVolume2,
  FiVolumeX,
  FiChevronDown,
  FiGrid,
  FiInfo,
} from "react-icons/fi";
import CameraStreamHLSIntegrated from "../../components/CameraStreamHLSIntegrated";
import CameraStreamWebRTC from "../../components/CameraStreamWebRTC";

// Phase 1 Sub-components
import TimelineController from "./components/TimelineController";
import PlaybackControls from "./components/PlaybackControls";
import ExportDialog from "./components/ExportDialog";
import SnapshotButton from "./components/SnapshotButton";

/* ─── Constants ─── */
const BOOKMARKS_KEY = "viewmax_camera_bookmarks_v1";
const RECENT_THRESHOLD_MINUTES = 10;
const SPEED_OPTIONS = [0.25, 0.5, 1, 2, 4, 8, 16];

/* ─── Bookmark persistence ─── */
function loadBookmarks(cameraId) {
  try {
    const raw = localStorage.getItem(BOOKMARKS_KEY);
    return raw ? JSON.parse(raw)[cameraId] || [] : [];
  } catch { return []; }
}
function saveBookmarks(cameraId, bookmarks) {
  try {
    const raw = localStorage.getItem(BOOKMARKS_KEY);
    const all = raw ? JSON.parse(raw) : {};
    all[cameraId] = bookmarks;
    localStorage.setItem(BOOKMARKS_KEY, JSON.stringify(all));
  } catch {}
}

/* ─── Helpers ─── */
function formatTimecode(date, tz = "America/Vancouver") {
  if (!date) return "--:--:--";
  return new Date(date).toLocaleTimeString("en-CA", {
    hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false, timeZone: tz,
  });
}
function formatDuration(seconds) {
  if (!seconds || seconds < 0) return "0:00";
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${s.toString().padStart(2, "0")}`;
}

/* ═══════════════════════════════════════════════════════════
   MAIN COMPONENT
   ═══════════════════════════════════════════════════════════ */
export default function EnterpriseDVRPlayer({
  cameraId,
  camera,
  date,
  timezone = "America/Vancouver",
  fullRecordings,
  detections,
  concatFileUrl,
  concatSegments,
  cameraList,
  onSwitchCamera,
  mode,
  setMode,
}) {
  /* ─── Core State ─── */
  const [playbackSource, setPlaybackSource] = useState(null);
  const [liveError, setLiveError] = useState(null);
  const [activeDetection, setActiveDetection] = useState(null);
  const [playbackDate, setPlaybackDate] = useState(null);
  const [bookmarks, setBookmarks] = useState([]);

  /* ─── Playback State ─── */
  const [playing, setPlaying] = useState(false);
  const [playbackSpeed, setPlaybackSpeed] = useState(1);
  const [currentVideoTime, setCurrentVideoTime] = useState(0);
  const [videoDuration, setVideoDuration] = useState(0);
  const [muted, setMuted] = useState(false);
  const [volume, setVolume] = useState(1);

  /* ─── UI State ─── */
  const [showSidebar, setShowSidebar] = useState(false);
  const [sidebarTab, setSidebarTab] = useState("events");
  const [showOverlay, setShowOverlay] = useState(true);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isMobile, setIsMobile] = useState(typeof window !== "undefined" ? window.innerWidth < 768 : false);

  /* ─── Digital Zoom ─── */
  const [zoomLevel, setZoomLevel] = useState(1);
  const [zoomOrigin, setZoomOrigin] = useState({ x: 50, y: 50 }); // percentage
  const [isPanning, setIsPanning] = useState(false);

  /* ─── Export State ─── */
  const [showExportDialog, setShowExportDialog] = useState(false);
  const [exportInPoint, setExportInPoint] = useState(null);
  const [exportOutPoint, setExportOutPoint] = useState(null);

  /* ─── Search & Filter ─── */
  const [searchTerm, setSearchTerm] = useState("");
  const [eventTypeFilter, setEventTypeFilter] = useState("all");

  /* ─── OSD (On Screen Display) ─── */
  const [showOSD, setShowOSD] = useState(true);
  const [osdMessage, setOsdMessage] = useState(null);

  /* ─── Refs ─── */
  const videoRef = useRef(null);
  const containerRef = useRef(null);
  const videoContainerRef = useRef(null);
  const overlayTimeoutRef = useRef(null);
  const timeUpdateRef = useRef(null);

  const hasConcat = !!concatFileUrl;
  const hasSegments = Array.isArray(concatSegments) && concatSegments.length > 0;

  /* ═══ RESPONSIVE ═══ */
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  /* ═══ FULLSCREEN TRACKING ═══ */
  useEffect(() => {
    const onChange = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener("fullscreenchange", onChange);
    return () => document.removeEventListener("fullscreenchange", onChange);
  }, []);

  /* ═══ AUTO-HIDE OVERLAY (desktop only) ═══ */
  useEffect(() => {
    if (isMobile) { setShowOverlay(true); return; }

    const showAndScheduleHide = () => {
      setShowOverlay(true);
      clearTimeout(overlayTimeoutRef.current);
      overlayTimeoutRef.current = setTimeout(() => setShowOverlay(false), 2500);
    };

    const el = containerRef.current;
    if (el) {
      el.addEventListener("mousemove", showAndScheduleHide);
      el.addEventListener("mouseenter", showAndScheduleHide);
      el.addEventListener("mouseleave", () => setShowOverlay(false));
      showAndScheduleHide();
    }
    return () => {
      clearTimeout(overlayTimeoutRef.current);
      if (el) {
        el.removeEventListener("mousemove", showAndScheduleHide);
        el.removeEventListener("mouseenter", showAndScheduleHide);
        el.removeEventListener("mouseleave", () => {});
      }
    };
  }, [isMobile]);

  /* ═══ KEYBOARD SHORTCUTS ═══ */
  useEffect(() => {
    const onKey = (e) => {
      // Don't capture if user is typing in an input
      if (e.target.tagName === "INPUT" || e.target.tagName === "TEXTAREA" || e.target.tagName === "SELECT") return;

      const video = videoRef.current;

      switch (e.key) {
        case " ":
        case "k":
        case "K":
          e.preventDefault();
          if (mode === "playback") setPlaying((p) => !p);
          break;

        case "ArrowLeft":
          e.preventDefault();
          if (!video) break;
          if (e.shiftKey) {
            video.currentTime = Math.max(0, video.currentTime - 10);
            flashOSD("−10s");
          } else {
            video.currentTime = Math.max(0, video.currentTime - (1 / 30));
            flashOSD("◁ Frame");
          }
          break;

        case "ArrowRight":
          e.preventDefault();
          if (!video) break;
          if (e.shiftKey) {
            video.currentTime = Math.min(video.duration, video.currentTime + 10);
            flashOSD("+10s");
          } else {
            video.currentTime = Math.min(video.duration, video.currentTime + (1 / 30));
            flashOSD("Frame ▷");
          }
          break;

        case "j":
        case "J":
          e.preventDefault();
          cycleSpeed(-1);
          break;

        case "l":
        case "L":
          if (!e.ctrlKey && !e.metaKey) {
            e.preventDefault();
            cycleSpeed(1);
          }
          break;

        case "ArrowUp":
          e.preventDefault();
          cycleSpeed(1);
          break;

        case "ArrowDown":
          e.preventDefault();
          cycleSpeed(-1);
          break;

        case "m":
        case "M":
          e.preventDefault();
          setMuted((m) => !m);
          flashOSD(muted ? "🔊 Unmuted" : "🔇 Muted");
          break;

        case "f":
        case "F":
          e.preventDefault();
          toggleFullscreen();
          break;

        case "b":
        case "B":
          e.preventDefault();
          handleAddBookmark();
          flashOSD("★ Bookmarked");
          break;

        case "s":
        case "S":
          if (!e.ctrlKey && !e.metaKey) {
            e.preventDefault();
            // Snapshot — handled by SnapshotButton but we flash OSD
            flashOSD("📷 Snapshot");
          }
          break;

        case "Escape":
          if (showExportDialog) setShowExportDialog(false);
          else if (showSidebar) setShowSidebar(false);
          else if (isFullscreen) document.exitFullscreen();
          break;

        case "e":
        case "E":
          e.preventDefault();
          setShowSidebar((s) => !s);
          break;

        case "i":
        case "I":
          if (!e.ctrlKey && !e.metaKey) {
            e.preventDefault();
            if (mode === "playback") {
              setExportInPoint(playbackDate || new Date());
              flashOSD("IN point set");
            }
          }
          break;

        case "o":
        case "O":
          if (!e.ctrlKey && !e.metaKey) {
            e.preventDefault();
            if (mode === "playback") {
              setExportOutPoint(playbackDate || new Date());
              flashOSD("OUT point set");
            }
          }
          break;

        case "+":
        case "=":
          e.preventDefault();
          handleZoom(1);
          break;

        case "-":
        case "_":
          e.preventDefault();
          handleZoom(-1);
          break;

        case "0":
          e.preventDefault();
          resetZoom();
          break;

        default:
          break;
      }
    };

    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [mode, playing, muted, playbackSpeed, showExportDialog, showSidebar, isFullscreen, playbackDate, zoomLevel]);

  /* ═══ VIDEO SYNC ═══ */
  useEffect(() => {
    const video = videoRef.current;
    if (!video || mode !== "playback") return;
    video.playbackRate = playbackSpeed;
  }, [playbackSpeed, mode]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    if (playing) video.play().catch(() => {});
    else video.pause();
  }, [playing]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    video.muted = muted;
    video.volume = volume;
  }, [muted, volume]);

  // Track video time for timeline sync
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const onTimeUpdate = () => {
      setCurrentVideoTime(video.currentTime);
      setVideoDuration(video.duration || 0);
    };
    const onLoadedMetadata = () => setVideoDuration(video.duration || 0);

    video.addEventListener("timeupdate", onTimeUpdate);
    video.addEventListener("loadedmetadata", onLoadedMetadata);
    return () => {
      video.removeEventListener("timeupdate", onTimeUpdate);
      video.removeEventListener("loadedmetadata", onLoadedMetadata);
    };
  }, [mode, playbackSource]);

  /* ═══ BOOKMARKS ═══ */
  useEffect(() => {
    setBookmarks(loadBookmarks(cameraId));
  }, [cameraId]);

  /* ═══ RESET ON DATE CHANGE ═══ */
  useEffect(() => {
    setPlaybackSource(null);
    setActiveDetection(null);
    setPlaybackDate(null);
    setMode("live");
    setPlaying(false);
    resetZoom();
  }, [date, setMode]);

  /* ─── OSD Flash ─── */
  const flashOSD = useCallback((msg) => {
    setOsdMessage(msg);
    clearTimeout(timeUpdateRef.current);
    timeUpdateRef.current = setTimeout(() => setOsdMessage(null), 1200);
  }, []);

  /* ─── Speed Cycling ─── */
  const cycleSpeed = useCallback((dir) => {
    setPlaybackSpeed((prev) => {
      const idx = SPEED_OPTIONS.indexOf(prev);
      const next = idx + dir;
      if (next < 0 || next >= SPEED_OPTIONS.length) return prev;
      const newSpeed = SPEED_OPTIONS[next];
      flashOSD(`${newSpeed}×`);
      return newSpeed;
    });
  }, [flashOSD]);

  /* ─── Digital Zoom ─── */
  const handleZoom = useCallback((dir) => {
    setZoomLevel((prev) => {
      const next = dir > 0 ? Math.min(prev * 1.5, 8) : Math.max(prev / 1.5, 1);
      if (next === 1) setZoomOrigin({ x: 50, y: 50 });
      flashOSD(`${next.toFixed(1)}×`);
      return next;
    });
  }, [flashOSD]);

  const resetZoom = useCallback(() => {
    setZoomLevel(1);
    setZoomOrigin({ x: 50, y: 50 });
  }, []);

  const handleVideoWheel = useCallback((e) => {
    if (!e.ctrlKey && !e.metaKey) return;
    e.preventDefault();
    const rect = videoContainerRef.current?.getBoundingClientRect();
    if (rect) {
      const x = ((e.clientX - rect.left) / rect.width) * 100;
      const y = ((e.clientY - rect.top) / rect.height) * 100;
      setZoomOrigin({ x, y });
    }
    handleZoom(e.deltaY < 0 ? 1 : -1);
  }, [handleZoom]);

  const handleVideoPan = useCallback((e) => {
    if (zoomLevel <= 1 || !isPanning) return;
    const rect = videoContainerRef.current?.getBoundingClientRect();
    if (!rect) return;
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    setZoomOrigin({ x: Math.max(0, Math.min(100, x)), y: Math.max(0, Math.min(100, y)) });
  }, [zoomLevel, isPanning]);

  /* ─── Stream error ─── */
  const handleStreamError = useCallback(() => setLiveError("stream_failed"), []);

  /* ─── Detection helpers ─── */
  const isDetectionRecent = (det) => {
    const ts = det.timestamp || det.start_time;
    if (!ts) return false;
    return (Date.now() - new Date(ts).getTime()) / 60000 < RECENT_THRESHOLD_MINUTES;
  };

  /* ─── Seek logic ─── */
  const findConcatOffsetForDate = (targetDate) => {
    if (!hasSegments) return null;
    const targetMs = new Date(targetDate.getTime() - targetDate.getTimezoneOffset() * 60000).getTime();
    for (const seg of concatSegments) {
      if (!seg.start_time || !seg.end_time) continue;
      const startMs = new Date(seg.start_time).getTime();
      const endMs = new Date(seg.end_time).getTime();
      if (targetMs >= startMs && targetMs <= endMs) {
        return (seg.concat_offset || 0) + Math.max(0, (targetMs - startMs) / 1000);
      }
    }
    return null;
  };

  const findClipForTimestamp = (targetDate) => {
    if (!fullRecordings?.length) return null;
    const targetMs = targetDate.getTime();
    const mapped = fullRecordings.map((f) => ({
      f, start: new Date(f.start_time).getTime(), end: new Date(f.start_time).getTime() + 5 * 60000,
    }));
    return mapped.find((m) => targetMs >= m.start && targetMs <= m.end)
      || mapped.sort((a, b) => Math.abs((a.start + a.end) / 2 - targetMs) - Math.abs((b.start + b.end) / 2 - targetMs))[0]
      || null;
  };

  const handleSeekTo = useCallback((targetDate) => {
    if (hasConcat && hasSegments) {
      const seekSeconds = findConcatOffsetForDate(targetDate);
      if (seekSeconds == null) return;
      setMode("playback");
      setPlaybackSource({ type: "concat", fileUrl: concatFileUrl, seekSeconds });
      setPlaybackDate(targetDate);
      setActiveDetection(null);
      setPlaying(true);
      return;
    }
    if (fullRecordings?.length) {
      const match = findClipForTimestamp(targetDate);
      if (!match) return;
      const { f, start, end } = match;
      const offset = Math.min(Math.max(targetDate.getTime() - start, 0), Math.max(end - start - 1000, 0)) / 1000;
      setMode("playback");
      setPlaybackSource({ type: "file", fileUrl: f.file_uri || f.file_url, seekSeconds: offset, _clipStartTime: f.start_time });
      setPlaybackDate(new Date(f.start_time));
      setActiveDetection(null);
      setPlaying(true);
    }
  }, [hasConcat, hasSegments, concatFileUrl, concatSegments, fullRecordings, setMode]);

  const handleDetectionSelect = useCallback((det) => {
    if (!det?.playback) return;
    setActiveDetection(det);
    const ts = det.timestamp ? new Date(det.timestamp) : null;
    const pb = det.playback;

    if (pb.mode === "timeline") {
      setMode("playback");
      setPlaybackSource({ type: "concat", fileUrl: pb.file_url || concatFileUrl, seekSeconds: pb.time_s });
      setPlaybackDate(ts);
      setPlaying(true);
    } else if (pb.mode === "segment" && ts) {
      handleSeekTo(ts);
    } else if (pb.mode === "clip" && pb.file_url) {
      setMode("playback");
      setPlaybackSource({ type: "file", fileUrl: pb.file_url, seekSeconds: 0, _clipStartTime: ts, isRecentFallback: true });
      setPlaybackDate(ts);
      setPlaying(true);
    }
  }, [concatFileUrl, handleSeekTo, setMode]);

  const handleBackToLive = useCallback(() => {
    setMode("live");
    setPlaybackSource(null);
    setActiveDetection(null);
    setPlaybackDate(null);
    setPlaying(false);
    resetZoom();
  }, [setMode, resetZoom]);

  const handleStartReview = useCallback(() => {
    if (concatFileUrl && concatSegments?.length) {
      setMode("playback");
      setPlaybackSource({ type: "concat", fileUrl: concatFileUrl, seekSeconds: 0 });
      setPlaybackDate(concatSegments[0]?.start_time ? new Date(concatSegments[0].start_time) : null);
    } else if (fullRecordings?.length) {
      const first = fullRecordings[0];
      setMode("playback");
      setPlaybackSource({ type: "file", fileUrl: first.file_uri || first.file_url, seekSeconds: 0, _clipStartTime: first.start_time });
      setPlaybackDate(new Date(first.start_time));
    }
    setPlaying(true);
  }, [concatFileUrl, concatSegments, fullRecordings, setMode]);

  /* ─── Frame step ─── */
  const handleFrameStep = useCallback((dir) => {
    const v = videoRef.current;
    if (!v) return;
    v.currentTime = Math.max(0, Math.min(v.currentTime + (dir / 30), v.duration));
    setPlaying(false);
  }, []);

  /* ─── Jump ─── */
  const handleJump = useCallback((seconds) => {
    const v = videoRef.current;
    if (!v) return;
    v.currentTime = Math.max(0, Math.min(v.currentTime + seconds, v.duration));
  }, []);

  /* ─── Export ─── */
  const handleExport = useCallback(async (options) => {
    // TODO: Backend FFmpeg clip extraction API
    if (playbackSource?.fileUrl) {
      const link = document.createElement("a");
      link.href = playbackSource.fileUrl;
      link.download = `${camera?.name || "camera"}_${(options.filename || "export")}.${options.format || "mp4"}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  }, [playbackSource, camera]);

  /* ─── Fullscreen ─── */
  const toggleFullscreen = useCallback(() => {
    if (!document.fullscreenElement) containerRef.current?.requestFullscreen();
    else document.exitFullscreen();
  }, []);

  /* ─── Bookmarks ─── */
  const handleAddBookmark = useCallback(() => {
    const ts = mode === "playback" && playbackDate ? playbackDate : new Date();
    const newBm = {
      id: `${cameraId}-${ts.getTime()}`,
      timestamp: ts.toISOString(),
      label: `Bookmark @ ${formatTimecode(ts, timezone)}`,
      mode,
    };
    const next = [...bookmarks, newBm];
    setBookmarks(next);
    saveBookmarks(cameraId, next);
  }, [bookmarks, cameraId, mode, playbackDate, timezone]);

  const handleRemoveBookmark = useCallback((bmId) => {
    const next = bookmarks.filter((b) => b.id !== bmId);
    setBookmarks(next);
    saveBookmarks(cameraId, next);
  }, [bookmarks, cameraId]);

  /* ─── Detections: sort, enrich, filter ─── */
  const enrichedDetections = useMemo(() => {
    return [...(detections || [])]
      .sort((a, b) => new Date(b.timestamp || b.start_time || 0) - new Date(a.timestamp || a.start_time || 0))
      .map((d) => ({ ...d, isRecent: isDetectionRecent(d) }));
  }, [detections]);

  const eventTypes = useMemo(() => {
    const types = new Set(enrichedDetections.map((d) => d.label || "Motion"));
    return ["all", ...Array.from(types)];
  }, [enrichedDetections]);

  const filteredDetections = useMemo(() => {
    let list = enrichedDetections;
    if (eventTypeFilter !== "all") list = list.filter((d) => (d.label || "Motion") === eventTypeFilter);
    if (searchTerm) {
      const q = searchTerm.toLowerCase();
      list = list.filter((d) => (d.label || "").toLowerCase().includes(q) || (d.zone || "").toLowerCase().includes(q));
    }
    return list;
  }, [enrichedDetections, eventTypeFilter, searchTerm]);

  /* ─── Recording coverage (for status bar) ─── */
  const recordingStats = useMemo(() => {
    if (!fullRecordings?.length) return { count: 0, totalHours: 0, gapCount: 0 };
    const totalMs = fullRecordings.reduce((sum, r) => {
      const start = new Date(r.start_time).getTime();
      const end = r.end_time ? new Date(r.end_time).getTime() : start + 5 * 60000;
      return sum + (end - start);
    }, 0);
    // Count gaps > 2 minutes
    let gaps = 0;
    for (let i = 1; i < fullRecordings.length; i++) {
      const prevEnd = fullRecordings[i - 1].end_time
        ? new Date(fullRecordings[i - 1].end_time).getTime()
        : new Date(fullRecordings[i - 1].start_time).getTime() + 5 * 60000;
      const nextStart = new Date(fullRecordings[i].start_time).getTime();
      if (nextStart - prevEnd > 2 * 60000) gaps++;
    }
    return { count: fullRecordings.length, totalHours: (totalMs / 3600000).toFixed(1), gapCount: gaps };
  }, [fullRecordings]);

  /* ═══════════════════════════════════════════════════════════
     RENDER
     ═══════════════════════════════════════════════════════════ */
  return (
    <div
      ref={containerRef}
      className={`relative w-full bg-[#0a0a0a] rounded-lg overflow-hidden flex flex-col ${
        isFullscreen ? "fixed inset-0 z-[9999] rounded-none" : ""
      }`}
      style={{ maxHeight: isFullscreen ? "100vh" : undefined }}
    >
      {/* ─── VIDEO VIEWPORT ─── */}
      <div
        ref={videoContainerRef}
        className="relative w-full bg-black flex-1"
        style={{
          aspectRatio: isFullscreen ? undefined : "16/9",
          minHeight: isFullscreen ? 0 : "360px",
          maxHeight: isFullscreen ? "calc(100vh - 120px)" : "75vh",
          cursor: zoomLevel > 1 ? (isPanning ? "grabbing" : "grab") : "default",
        }}
        onWheel={handleVideoWheel}
        onMouseDown={() => zoomLevel > 1 && setIsPanning(true)}
        onMouseUp={() => setIsPanning(false)}
        onMouseLeave={() => setIsPanning(false)}
        onMouseMove={handleVideoPan}
      >
        {/* Video layer with digital zoom */}
        <div
          className="absolute inset-0 transition-transform duration-100"
          style={{
            transform: `scale(${zoomLevel})`,
            transformOrigin: `${zoomOrigin.x}% ${zoomOrigin.y}%`,
          }}
        >
          {mode === "live" && liveError ? (
            /* ─── Error State ─── */
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="text-center px-6">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-yellow-500/10 flex items-center justify-center">
                  <FiAlertTriangle className="w-8 h-8 text-yellow-500" />
                </div>
                <p className="text-white font-medium mb-1">Stream Unavailable</p>
                <p className="text-sm text-gray-500 mb-5">Camera may be offline or unreachable</p>
                <button
                  onClick={() => { setLiveError(null); setMode("live"); }}
                  className="px-5 py-2 bg-white/10 hover:bg-white/20 border border-white/10 rounded text-sm text-white transition-colors"
                >
                  Retry Connection
                </button>
              </div>
            </div>
          ) : mode === "live" ? (
            /* ─── Live Stream ─── */
            <div className="absolute inset-0">
              <CameraStreamWebRTC
                path={`cam${cameraId}`}
                baseUrl="https://vision.viewmax.net"
                showControls={false}
                onFallback={() => setLiveError("webrtc_failed")}
              />
            </div>
          ) : (
            /* ─── Playback ─── */
            <div className="absolute inset-0">
              <CameraStreamHLSIntegrated
                key={`${cameraId}-${mode}-${playbackDate || "live"}`}
                camId={cameraId}
                mtxPath={`cam${cameraId}`}
                baseUrl="https://vision.viewmax.net"
                wsBaseUrl="wss://vision.viewmax.net"
                isLive={false}
                playbackSource={playbackSource}
                date={date}
                timezone={timezone}
                fullRecordings={fullRecordings}
                detections={enrichedDetections}
                playbackDate={playbackDate}
                onDetectionClick={handleDetectionSelect}
                onTimelineSeek={handleSeekTo}
                onError={handleStreamError}
                ref={videoRef}
              />
            </div>
          )}
        </div>

        {/* ─── OSD: On Screen Display ─── */}
        {osdMessage && (
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-40 pointer-events-none">
            <div className="px-6 py-3 bg-black/80 backdrop-blur-md rounded-lg text-white text-lg font-mono tracking-wider animate-pulse">
              {osdMessage}
            </div>
          </div>
        )}

        {/* ─── LIVE badge ─── */}

        {/* ─── Playback timestamp OSD ─── */}
        {mode === "playback" && showOSD && (
          <div className="absolute top-3 left-3 z-30 flex items-center gap-2">
            <div className="flex items-center gap-1.5 px-2.5 py-1 bg-blue-600/90 rounded text-white text-xs font-semibold tracking-wide uppercase">
              <FiClock className="w-3 h-3" />
              REC
            </div>
            <span className="text-white/90 text-xs font-mono bg-black/50 px-2 py-0.5 rounded">
              {formatTimecode(playbackDate, timezone)}
            </span>
            {playbackSpeed !== 1 && (
              <span className="text-yellow-400 text-xs font-mono bg-black/50 px-2 py-0.5 rounded">
                {playbackSpeed}×
              </span>
            )}
          </div>
        )}

        {/* ─── Zoom indicator ─── */}
        {zoomLevel > 1 && (
          <div className="absolute top-3 right-3 z-30">
            <div className="flex items-center gap-1 px-2 py-1 bg-black/70 backdrop-blur-sm rounded text-white text-xs font-mono">
              <FiZoomIn className="w-3 h-3" />
              {zoomLevel.toFixed(1)}× 
              <button onClick={resetZoom} className="ml-1 text-gray-400 hover:text-white">
                <FiX className="w-3 h-3" />
              </button>
            </div>
          </div>
        )}

        {/* ─── OVERLAY CONTROLS ─── */}
        <div
          className={`absolute inset-0 z-20 pointer-events-none transition-opacity duration-300 ${
            showOverlay || isMobile ? "opacity-100" : "opacity-0"
          }`}
        >
          {/* Top gradient bar */}
          <div className="absolute top-0 left-0 right-0 h-16 bg-gradient-to-b from-black/70 to-transparent pointer-events-auto">
            <div className="flex items-center justify-between px-3 py-2 h-full">
              {/* Camera info */}
              <div className="flex items-center gap-3 min-w-0">
                <h3 className="text-white text-sm font-medium truncate">
                  {camera?.name || `Camera ${cameraId}`}
                </h3>
                {camera?.location && (
                  <span className="text-gray-400 text-xs truncate hidden sm:inline">
                    {camera.location}
                  </span>
                )}
              </div>

              {/* Top-right actions */}
              <div className="flex items-center gap-1.5">
                {/* Camera switcher */}
                {cameraList?.length > 1 && (
                  <div className="relative">
                    <select
                      className="appearance-none bg-white/10 hover:bg-white/20 border border-white/10 rounded px-3 py-1.5 pr-7 text-xs text-white focus:outline-none focus:ring-1 focus:ring-blue-500/50 cursor-pointer transition-colors"
                      value={String(cameraId)}
                      onChange={(e) => onSwitchCamera?.(e.target.value)}
                    >
                      {cameraList.map((c) => (
                        <option key={c.id} value={c.id} className="bg-gray-900">
                          {c.name || `Camera ${c.id}`}
                        </option>
                      ))}
                    </select>
                    <FiChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-3 h-3 text-gray-400 pointer-events-none" />
                  </div>
                )}

                {/* Mode toggle */}
                {mode === "live" ? (
                  <button onClick={handleStartReview} className="vms-btn" title="Review recordings (playback)">
                    <FiClock className="w-3.5 h-3.5" />
                    <span className="hidden sm:inline">Review</span>
                  </button>
                ) : (
                  <button onClick={handleBackToLive} className="vms-btn" title="Return to live view">
                    <span className="w-2 h-2 bg-red-500 rounded-full" />
                    <span className="hidden sm:inline">Live</span>
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Bottom gradient bar */}
          <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent pointer-events-auto">
            <div className="flex items-center justify-between px-3 py-2">
              {/* Left actions */}
              <div className="flex items-center gap-1">
                {/* Volume */}
                <button
                  onClick={() => setMuted(!muted)}
                  className="vms-icon-btn"
                  title={muted ? "Unmute (M)" : "Mute (M)"}
                >
                  {muted ? <FiVolumeX className="w-4 h-4" /> : <FiVolume2 className="w-4 h-4" />}
                </button>

                {/* Bookmark */}
                <button onClick={handleAddBookmark} className="vms-icon-btn" title="Add bookmark (B)">
                  <FiBookmark className="w-4 h-4" />
                </button>

                {mode === "playback" && (
                  <>
                    {/* Snapshot */}
                    <SnapshotButton videoRef={videoRef} camera={camera} currentTime={playbackDate} />

                    {/* Export */}
                    <button onClick={() => setShowExportDialog(true)} className="vms-icon-btn" title="Export clip (I/O to set points)">
                      <FiScissors className="w-4 h-4" />
                    </button>

                    {/* Download */}
                    {playbackSource?.fileUrl && (
                      <button
                        onClick={() => {
                          const a = document.createElement("a");
                          a.href = playbackSource.fileUrl;
                          a.download = `${camera?.name || "cam"}_${(playbackDate || new Date()).toISOString().slice(0, 19).replace(/:/g, "-")}.mp4`;
                          a.click();
                        }}
                        className="vms-icon-btn"
                        title="Download recording"
                      >
                        <FiDownload className="w-4 h-4" />
                      </button>
                    )}
                  </>
                )}
              </div>

              {/* Right actions */}
              <div className="flex items-center gap-1">
                {/* Zoom controls */}
                <button onClick={() => handleZoom(-1)} className="vms-icon-btn" title="Zoom out (−)" disabled={zoomLevel <= 1}>
                  <FiZoomOut className="w-4 h-4" />
                </button>
                <button onClick={() => handleZoom(1)} className="vms-icon-btn" title="Zoom in (+)">
                  <FiZoomIn className="w-4 h-4" />
                </button>

                {/* Events sidebar */}
                <button
                  onClick={() => setShowSidebar(!showSidebar)}
                  className={`vms-icon-btn ${showSidebar ? "!bg-white !text-black" : ""}`}
                  title="Events panel (E)"
                >
                  <FiMenu className="w-4 h-4" />
                </button>

                {/* Fullscreen */}
                <button onClick={toggleFullscreen} className="vms-icon-btn" title="Fullscreen (F)">
                  {isFullscreen ? <FiMinimize className="w-4 h-4" /> : <FiMaximize className="w-4 h-4" />}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ─── PLAYBACK CONTROLS BAR ─── */}
      {mode === "playback" && (
        <PlaybackControls
          playing={playing}
          onPlayPause={() => setPlaying(!playing)}
          playbackSpeed={playbackSpeed}
          onSpeedChange={setPlaybackSpeed}
          onFrameStep={handleFrameStep}
          onJump={handleJump}
          currentTime={currentVideoTime}
          duration={videoDuration}
          disabled={!playbackSource}
        />
      )}

      {/* ─── TIMELINE ─── */}
      {mode === "playback" && (
        <TimelineController
          date={date}
          currentTime={playbackDate}
          onSeek={handleSeekTo}
          recordings={fullRecordings}
          detections={enrichedDetections}
          timezone={timezone}
        />
      )}

      {/* ─── RECORDING STATUS BAR ─── */}
      <div className="flex items-center justify-between px-3 py-1.5 bg-[#111] border-t border-white/5 text-xs text-gray-500">
        <div className="flex items-center gap-4">
          <span>
            <span className="text-gray-400">{recordingStats.count}</span> recordings
          </span>
          <span>
            <span className="text-gray-400">{recordingStats.totalHours}</span>h coverage
          </span>
          {recordingStats.gapCount > 0 && (
            <span className="text-yellow-500/70">
              {recordingStats.gapCount} gap{recordingStats.gapCount > 1 ? "s" : ""}
            </span>
          )}
        </div>
        <div className="flex items-center gap-3">
          <span className="text-gray-600">{date}</span>
          {mode === "playback" && videoDuration > 0 && (
            <span className="font-mono text-gray-400">
              {formatDuration(currentVideoTime)} / {formatDuration(videoDuration)}
            </span>
          )}
        </div>
      </div>

      {/* ─── EXPORT DIALOG ─── */}
      <ExportDialog
        isOpen={showExportDialog}
        onClose={() => setShowExportDialog(false)}
        inPoint={exportInPoint}
        outPoint={exportOutPoint}
        currentTime={playbackDate}
        onSetInPoint={setExportInPoint}
        onSetOutPoint={setExportOutPoint}
        onClearPoints={() => { setExportInPoint(null); setExportOutPoint(null); }}
        onExport={handleExport}
        camera={camera}
      />

      {/* ─── EVENTS SIDEBAR ─── */}
      {showSidebar && (
        <div
          className={`absolute top-0 right-0 bottom-0 z-50 bg-[#0d0d0d]/98 backdrop-blur-xl border-l border-white/8 flex flex-col ${
            isMobile ? "w-full" : "w-80"
          }`}
        >
          {/* Sidebar header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-white/8">
            <div className="flex gap-0.5 bg-white/5 rounded-lg p-0.5">
              {[
                { key: "events", label: "Events", count: filteredDetections.length },
                { key: "bookmarks", label: "Marks", count: bookmarks.length },
              ].map((tab) => (
                <button
                  key={tab.key}
                  onClick={() => setSidebarTab(tab.key)}
                  className={`px-3 py-1.5 text-xs font-medium rounded-md transition-all ${
                    sidebarTab === tab.key
                      ? "bg-white text-black shadow-sm"
                      : "text-gray-400 hover:text-white"
                  }`}
                >
                  {tab.label}
                  <span className="ml-1.5 text-[10px] opacity-60">{tab.count}</span>
                </button>
              ))}
            </div>
            <button onClick={() => setShowSidebar(false)} className="text-gray-500 hover:text-white transition-colors p-1">
              <FiX className="w-4 h-4" />
            </button>
          </div>

          {/* Events tab */}
          {sidebarTab === "events" && (
            <>
              {/* Search + filter */}
              <div className="p-3 space-y-2 border-b border-white/5">
                <div className="relative">
                  <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-500" />
                  <input
                    type="text"
                    placeholder="Search events..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full bg-white/5 border border-white/8 rounded-lg pl-9 pr-3 py-2 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-blue-500/40 transition-colors"
                  />
                </div>
                {eventTypes.length > 2 && (
                  <div className="flex gap-1 flex-wrap">
                    {eventTypes.map((type) => (
                      <button
                        key={type}
                        onClick={() => setEventTypeFilter(type)}
                        className={`px-2 py-0.5 text-[10px] font-medium rounded-full transition-colors ${
                          eventTypeFilter === type
                            ? "bg-blue-500/20 text-blue-400 border border-blue-500/30"
                            : "bg-white/5 text-gray-500 border border-transparent hover:text-gray-300"
                        }`}
                      >
                        {type === "all" ? "All" : type}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Event list */}
              <div className="flex-1 overflow-y-auto">
                {filteredDetections.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-16 text-gray-600">
                    <FiSearch className="w-8 h-8 mb-3 opacity-30" />
                    <p className="text-sm">No events found</p>
                  </div>
                ) : (
                  <div className="p-2 space-y-0.5">
                    {filteredDetections.slice(0, 100).map((det) => {
                      const ts = det.timestamp || det.start_time;
                      const isActive = activeDetection?.id === det.id;
                      return (
                        <button
                          key={det.id || ts || Math.random()}
                          onClick={() => handleDetectionSelect(det)}
                          className={`w-full text-left px-3 py-2.5 rounded-lg transition-all group ${
                            isActive
                              ? "bg-blue-500/15 border border-blue-500/25"
                              : "hover:bg-white/5 border border-transparent"
                          }`}
                        >
                          <div className="flex items-center justify-between gap-2">
                            <div className="flex items-center gap-2 min-w-0">
                              {det.isRecent && (
                                <span className="w-1.5 h-1.5 rounded-full bg-orange-500 shrink-0 animate-pulse" />
                              )}
                              <span className={`text-sm truncate ${isActive ? "text-white font-medium" : "text-gray-300 group-hover:text-white"}`}>
                                {det.label || "Motion"}
                              </span>
                            </div>
                            <span className="text-[11px] font-mono text-gray-500 shrink-0 tabular-nums">
                              {ts ? formatTimecode(new Date(ts), timezone) : ""}
                            </span>
                          </div>
                          {det.zone && (
                            <p className="text-[11px] text-gray-600 mt-0.5 ml-3.5 truncate">
                              {det.zone}
                            </p>
                          )}
                        </button>
                      );
                    })}
                    {filteredDetections.length > 100 && (
                      <p className="text-center text-xs text-gray-600 py-3">
                        +{filteredDetections.length - 100} more events
                      </p>
                    )}
                  </div>
                )}
              </div>
            </>
          )}

          {/* Bookmarks tab */}
          {sidebarTab === "bookmarks" && (
            <div className="flex-1 overflow-y-auto">
              {bookmarks.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-16 text-gray-600">
                  <FiBookmark className="w-8 h-8 mb-3 opacity-30" />
                  <p className="text-sm mb-1">No bookmarks yet</p>
                  <p className="text-xs text-gray-700">Press <kbd className="px-1.5 py-0.5 bg-white/10 rounded text-[10px] font-mono">B</kbd> to bookmark</p>
                </div>
              ) : (
                <div className="p-2 space-y-0.5">
                  {bookmarks.slice().reverse().map((bm) => (
                    <div
                      key={bm.id}
                      className="flex items-center justify-between px-3 py-2.5 rounded-lg hover:bg-white/5 transition-colors group"
                    >
                      <button
                        onClick={() => handleSeekTo(new Date(bm.timestamp))}
                        className="flex-1 text-left min-w-0"
                      >
                        <div className="text-sm text-gray-300 group-hover:text-white truncate">
                          {bm.label}
                        </div>
                        <div className="text-[11px] font-mono text-gray-600 mt-0.5">
                          {new Date(bm.timestamp).toLocaleString("en-CA", {
                            month: "short", day: "numeric", hour: "2-digit", minute: "2-digit", timeZone: timezone,
                          })}
                        </div>
                      </button>
                      <button
                        onClick={() => handleRemoveBookmark(bm.id)}
                        className="text-gray-700 hover:text-red-400 transition-colors ml-2 opacity-0 group-hover:opacity-100"
                      >
                        <FiX className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* ─── INLINE STYLES for VMS buttons ─── */}
      <style>{`
        .vms-btn {
          display: flex;
          align-items: center;
          gap: 6px;
          padding: 4px 12px;
          background: rgba(255,255,255,0.08);
          border: 1px solid rgba(255,255,255,0.08);
          border-radius: 6px;
          color: white;
          font-size: 12px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.15s;
          backdrop-filter: blur(8px);
        }
        .vms-btn:hover {
          background: rgba(255,255,255,0.15);
          border-color: rgba(255,255,255,0.15);
        }
        .vms-icon-btn {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 32px;
          height: 32px;
          background: transparent;
          border: none;
          border-radius: 6px;
          color: rgba(255,255,255,0.7);
          cursor: pointer;
          transition: all 0.15s;
        }
        .vms-icon-btn:hover {
          background: rgba(255,255,255,0.1);
          color: white;
        }
        .vms-icon-btn:disabled {
          opacity: 0.3;
          cursor: not-allowed;
        }
        .vms-icon-btn:disabled:hover {
          background: transparent;
        }
      `}</style>
    </div>
  );
}
